import React from 'react';
import { Users, Award, Scale, Target } from 'lucide-react';
import khurram from '../assets/images/khurram_main.jpg';

// Simple Card component to ensure consistent styling
const Card = ({ children, className }) => {
  return (
    <div className={`bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 p-8 ${className}`}>
      {children}
    </div>
  );
};

const About = () => {
    const stats = [
        { title: 'Years Experience', value: '20+', icon: Scale, color: 'text-indigo-600', bgColor: 'bg-indigo-50' },
        { title: 'Cases Won', value: '1000+', icon: Award, color: 'text-blue-600', bgColor: 'bg-blue-50' },
        { title: 'Happy Clients', value: '2500+', icon: Users, color: 'text-green-600', bgColor: 'bg-green-50' },
        { title: 'Success Rate', value: '98%', icon: Target, color: 'text-red-600', bgColor: 'bg-red-50' },
    ];

    return (
        <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
            {/* Bismillah Section */}
            <div className="bg-white py-24 text-center border-b relative overflow-hidden">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiM2MzY2ZjEiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDM0djZoNnYtNmgtNnptNiA2djZoNnYtNmgtNnptLTEyIDBoNnY2aC02di02em0xMiAwaDZ2NmgtNnYtNnoiLz48cGF0aCBkPSJNMTIgMTJoNnY2aC02di02em02IDZoNnY2aC02di02em0wLTZoNnY2aC02di02em0xMiAwaDZ2NmgtNnYtNnptLTEyIDEyaDZ2NmgtNnYtNnptMTIgMGg2djZoLTZ2LTZ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-50"></div>
                <div className="container mx-auto px-4 relative z-10">
                    <h1 className="text-3xl md:text-5xl font-arabic mb-4 text-indigo-600 animate-fade-in">
                        بِسْمِ ٱللَّٰهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ
                    </h1>
                    <p className="text-lg text-gray-600 max-w-2xl mx-auto px-4">
                        In the name of Allah, the Most Gracious, the Most Merciful
                    </p>
                    <div className="mt-10 max-w-3xl mx-auto px-8 py-6 bg-indigo-50 rounded-xl relative">
                        <div className="absolute top-0 bottom-0 left-4 w-1 bg-indigo-500 rounded-full"></div>
                        <p className="text-lg text-gray-700 italic">
                            "O you who believe! Stand out firmly for justice, as witnesses to Allah, 
                            even if it be against yourselves, your parents, and your relatives." 
                            <span className="block mt-2 text-right font-medium text-indigo-600">- Surah An-Nisa, 4:135</span>
                        </p>
                    </div>
                </div>
            </div>

            {/* Hero Section with Image */}
            <div className="relative bg-gradient-to-r from-indigo-900 to-indigo-700 py-20">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNCI+PHBhdGggZD0iTTM2IDM0djZoNnYtNmgtNnptNiA2djZoNnYtNmgtNnptLTEyIDBoNnY2aC02di02em0xMiAwaDZ2NmgtNnYtNnoiLz48cGF0aCBkPSJNMTIgMTJoNnY2aC02di02em02IDZoNnY2aC02di02em0wLTZoNnY2aC02di02em0xMiAwaDZ2NmgtNnYtNnptLTEyIDEyaDZ2NmgtNnYtNnptMTIgMGg2djZoLTZ2LTZ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-50"></div>
                <div className="container mx-auto px-4 relative z-10">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                        {/* Text Content */}
                        <div className="text-white">
                            <div className="inline-block bg-white/20 text-white px-4 py-1 rounded-md text-sm font-medium mb-4 backdrop-blur-sm">
                                ABOUT OUR FIRM
                            </div>
                            <h2 className="text-4xl md:text-5xl font-bold mb-6">
                                About Our Firm
                            </h2>
                            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
                                Committed to excellence in legal services with Islamic principles 
                                of justice and integrity at our core. Our team of experienced attorneys
                                is dedicated to protecting your rights and ensuring the best possible outcome 
                                for your case.
                            </p>
                        </div>
                        {/* Lawyer Image */}
                        <div className="relative">
                            <div className="absolute -top-6 -left-6 right-6 bottom-6 bg-indigo-300/20 rounded-lg -z-10 backdrop-blur-sm"></div>
                            <img 
                                src={khurram} 
                                alt="Senior Lawyer" 
                                className="rounded-lg shadow-2xl w-full h-full object-cover max-h-[500px]"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Vision & Mission */}
            <div className="container mx-auto px-4 py-20">
                <div className="text-center mb-16">
                    <h2 className="text-3xl font-bold mb-4">Our Vision & Mission</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">
                        Guided by Islamic principles and professional excellence, we are committed to 
                        providing exceptional legal services to our clients.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                    <div className="group bg-white p-8 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border-t-4 border-indigo-600">
                        <div className="bg-indigo-50 text-indigo-600 p-4 inline-flex rounded-lg mb-6 transition-colors duration-300">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold mb-4 text-gray-800">Our Vision</h2>
                        <p className="text-gray-600 text-lg leading-relaxed">
                            To be the leading law firm known for excellence in legal services,
                            integrity, and commitment to client success, while upholding 
                            Islamic principles of justice and fairness.
                        </p>
                    </div>

                    <div className="group bg-white p-8 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border-t-4 border-indigo-600">
                        <div className="bg-indigo-50 text-indigo-600 p-4 inline-flex rounded-lg mb-6 transition-colors duration-300">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold mb-4 text-gray-800">Our Mission</h2>
                        <p className="text-gray-600 text-lg leading-relaxed">
                            To provide exceptional legal services with the highest standards of
                            professional ethics and Islamic values, helping our clients achieve 
                            their goals through strategic legal solutions and unwavering advocacy.
                        </p>
                    </div>
                </div>

                {/* Stats Section */}
                <div className="mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                    {stats.map((stat, index) => (
                        <div key={index} className="group">
                            <Card className="text-center hover:border-l-4 hover:border-indigo-600 transition-all group-hover:-translate-y-1 duration-300">
                                <div className={`${stat.bgColor} ${stat.color} p-4 inline-flex rounded-lg mb-6 transition-colors duration-300 mx-auto`}>
                                    <stat.icon className="w-12 h-12" />
                                </div>
                                <div className="text-4xl font-bold text-gray-800 mb-2 group-hover:text-indigo-600 transition-colors">
                                    {stat.value}
                                </div>
                                <div className="text-gray-600 font-medium">
                                    {stat.title}
                                </div>
                            </Card>
                        </div>
                    ))}
                </div>
            </div>

            {/* Core Values */}
            <div className="bg-gray-50 py-20">
                <div className="container mx-auto px-4">
                    <div className="max-w-3xl mx-auto text-center">
                        <div className="inline-block bg-indigo-100 text-indigo-800 px-4 py-1 rounded-md text-sm font-medium mb-4">
                            OUR PRINCIPLES
                        </div>
                        <h2 className="text-3xl font-bold mb-6">Core Values & Ethics</h2>
                        <p className="text-gray-600 mb-12">
                            Our legal practice is guided by these core Islamic and professional values that shape
                            our approach to every case and client relationship.
                        </p>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
                            <div className="bg-white p-6 rounded-xl shadow-sm">
                                <h3 className="text-xl font-bold mb-3 text-indigo-600">Justice & Integrity</h3>
                                <p className="text-gray-600">We advocate firmly for justice with unwavering integrity in all our dealings, ensuring fairness and ethical conduct.</p>
                            </div>
                            
                            <div className="bg-white p-6 rounded-xl shadow-sm">
                                <h3 className="text-xl font-bold mb-3 text-indigo-600">Compassion & Respect</h3>
                                <p className="text-gray-600">We treat all clients with compassion and respect, understanding the human dimension behind every legal challenge.</p>
                            </div>
                            
                            <div className="bg-white p-6 rounded-xl shadow-sm">
                                <h3 className="text-xl font-bold mb-3 text-indigo-600">Excellence & Diligence</h3>
                                <p className="text-gray-600">We pursue excellence through meticulous preparation and diligent effort, leaving no stone unturned in your case.</p>
                            </div>
                            
                            <div className="bg-white p-6 rounded-xl shadow-sm">
                                <h3 className="text-xl font-bold mb-3 text-indigo-600">Trust & Confidentiality</h3>
                                <p className="text-gray-600">We honor the trust placed in us by maintaining the strictest confidentiality and transparent communication.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default About;